import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

// Create the Octave context
const OctaveContext = createContext(null);

/**
 * Provider component that manages a single shared Octave worker instance
 * This ensures only one Octave WASM instance is loaded, improving performance
 */
export const OctaveProvider = ({ children }) => {
  const [isReady, setIsReady] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const workerRef = useRef(null);
  const executionIdRef = useRef(0);
  const callbacksRef = useRef({});
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize worker only when explicitly requested
  const initializeWorker = useCallback(() => {
    if (isInitialized || workerRef.current) {
      return; // Already initialized
    }

    console.log('[OctaveContext] Initializing Octave worker...');

    // Check for stale execution state from previous session/reload
    const wasExecuting = sessionStorage.getItem('octave_executing');
    if (wasExecuting === 'true') {
      console.log('[OctaveContext] Detected stale execution state, clearing...');
      sessionStorage.removeItem('octave_executing');
    }

    setIsInitialized(true);

    // Create the worker
    const worker = new Worker('/workers/octave-worker.js');
    workerRef.current = worker;

    // Handle messages from the worker
    worker.onmessage = (e) => {
      const { type, id, data, result, error, executionTimeMs } = e.data;

      switch (type) {
        case 'ready':
          console.log('[OctaveContext] Octave worker ready');
          setIsReady(true);
          break;

        case 'stdout':
          if (callbacksRef.current[id]?.onStdout) {
            callbacksRef.current[id].onStdout(data);
          }
          break;

        case 'stderr':
          if (callbacksRef.current[id]?.onStderr) {
            callbacksRef.current[id].onStderr(data);
          }
          break;

        case 'artifacts':
          if (callbacksRef.current[id]?.onArtifacts) {
            callbacksRef.current[id].onArtifacts(data);
          }
          break;

        case 'complete':
          setIsExecuting(false);
          sessionStorage.removeItem('octave_executing');
          if (callbacksRef.current[id]?.onComplete) {
            callbacksRef.current[id].onComplete(result, executionTimeMs);
          }
          delete callbacksRef.current[id];
          break;

        case 'error':
          setIsExecuting(false);
          sessionStorage.removeItem('octave_executing');
          if (callbacksRef.current[id]?.onError) {
            callbacksRef.current[id].onError(error, executionTimeMs);
          }
          delete callbacksRef.current[id];
          break;

        default:
          console.warn('[OctaveContext] Unknown message type from worker:', type);
      }
    };

    worker.onerror = (error) => {
      console.error('[OctaveContext] Worker error:', error);
      setIsReady(false);
      setIsExecuting(false);
    };
  }, [isInitialized]);

  // Cleanup worker on unmount
  useEffect(() => {
    return () => {
      if (workerRef.current) {
        console.log('[OctaveContext] Terminating Octave worker');
        workerRef.current.terminate();
      }
    };
  }, []);

  const execute = useCallback((code, callbacks = {}) => {
    if (!isReady) {
      callbacks.onError?.('Octave is not ready yet');
      return;
    }

    if (isExecuting) {
      callbacks.onError?.('Another execution is in progress');
      return;
    }

    const id = ++executionIdRef.current;
    callbacksRef.current[id] = callbacks;
    setIsExecuting(true);
    sessionStorage.setItem('octave_executing', 'true');

    workerRef.current.postMessage({
      type: 'execute',
      code,
      id
    });

    return id;
  }, [isReady, isExecuting]);

  // Cancel current execution by terminating and recreating the worker
  const cancelExecution = useCallback(() => {
    if (!isExecuting) return;

    console.log('[OctaveContext] Cancelling execution...');

    // Terminate the current worker
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null; // Clear the ref so initializeWorker can create a new one
    }

    // Clear all pending callbacks
    Object.keys(callbacksRef.current).forEach(id => {
      callbacksRef.current[id]?.onError?.('Execution cancelled by user');
      delete callbacksRef.current[id];
    });

    // Reset state
    setIsExecuting(false);
    sessionStorage.removeItem('octave_executing');
    setIsReady(false);

    // Reinitialize the worker
    setIsInitialized(false);
    setTimeout(() => {
      initializeWorker();
    }, 100);
  }, [isExecuting, initializeWorker]);

  // Force reset execution state (for recovery from stuck states)
  const resetExecutionState = useCallback(() => {
    console.log('[OctaveContext] Force resetting execution state...');
    setIsExecuting(false);

    // Clear all pending callbacks
    Object.keys(callbacksRef.current).forEach(id => {
      delete callbacksRef.current[id];
    });
  }, []);

  const value = {
    isReady,
    isExecuting,
    isInitialized,
    execute,
    cancelExecution,
    resetExecutionState,
    initializeWorker
  };

  return (
    <OctaveContext.Provider value={value}>
      {children}
    </OctaveContext.Provider>
  );
};

/**
 * Hook to access the shared Octave worker
 * Automatically initializes the worker on first use if not already initialized
 */
export const useOctave = () => {
  const context = useContext(OctaveContext);

  if (!context) {
    throw new Error('useOctave must be used within an OctaveProvider');
  }

  // Auto-initialize when hook is first used
  useEffect(() => {
    if (!context.isInitialized) {
      console.log('[useOctave] Auto-initializing Octave worker');
      context.initializeWorker();
    }
  }, [context]);

  return {
    isReady: context.isReady,
    isExecuting: context.isExecuting,
    execute: context.execute,
    cancelExecution: context.cancelExecution,
    resetExecutionState: context.resetExecutionState
  };
};
